/**
 * API do aluno (feed de cursos, ranking de pontos ativos).
 * GET …/api/v1/student/feed
 * GET …/api/v1/student/ranking
 */

import type {
  StudentRankingCurrentUser,
  StudentRankingEntry,
} from '@e-learning/shared';

/** Linha retornada pela API (top ou currentUser). */
export interface StudentRankingApiRow {
  rank: number;
  userId: string;
  name: string;
  activePoints: number;
}

/** Corpo JSON de GET v1/student/ranking. */
export interface StudentRankingApiResponse {
  top: StudentRankingApiRow[];
  currentUser?: StudentRankingApiRow | null;
}

export type FetchStudentRankingFn = () => Promise<StudentRankingApiResponse>;

/** Grupo de público-alvo associado a um curso no feed. */
export interface StudentFeedTargetGroup {
  groupId: string;
  groupName: string;
}

/** Curso em uma seção do feed (continuar, assistir de novo, disponíveis). */
export interface StudentFeedCourseItem {
  courseId: string;
  title: string;
  description: string;
  categoryName: string;
  coverImageUrl: string;
  totalPoints: number;
  isPublicAccess: boolean;
  targetUserId: string;
  targetGroups: StudentFeedTargetGroup[];
  totalModules: number;
  totalLessons: number;
  totalQuizzes: number;
  /**
   * Percentual de conclusão do curso (retornado no feed para cursos em andamento).
   * A API pode retornar `null`/`undefined` dependendo da seção.
   */
  porcentagem?: number | null;
  /**
   * Percentual de conclusão do curso (retornado no feed para cursos em andamento).
   * Nome mais recente retornado pela API.
   */
  progressPercentage?: number | null;
  /** Data de conclusão (ISO), quando a API enviar (ex.: secção watch again). */
  completedAt?: string | null;
}

/** Corpo JSON de GET v1/student/feed. */
export interface StudentFeedApiResponse {
  continuing: StudentFeedCourseItem[];
  watchAgain: StudentFeedCourseItem[];
  available: StudentFeedCourseItem[];
}

export type FetchStudentFeedFn = () => Promise<StudentFeedApiResponse>;

export type StudentCoursePlayerLessonType = 'VIDEO' | 'FILE' | 'QUIZ';
export type StudentCoursePlayerLessonStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'LOCKED';

export interface StudentCoursePlayerLesson {
  id: string;
  title: string;
  type: StudentCoursePlayerLessonType;
  videoUrl: string | null;
  displayOrder: number;
  status: StudentCoursePlayerLessonStatus;
  progressPercentage: number;
  lastPosition: number | null;
  points: number;
}

export interface StudentCoursePlayerModule {
  id: string;
  title: string;
  displayOrder: number;
  lessons: StudentCoursePlayerLesson[];
}

export interface StudentCoursePlayerApiResponse {
  courseId: string;
  title: string;
  lastAccessedLessonId: string | null;
  duration: number;
  totalPoints: number;
  categoryName: string;
  modules: StudentCoursePlayerModule[];
}

export type FetchStudentCoursePlayerFn = (
  courseId: string
) => Promise<StudentCoursePlayerApiResponse>;

export interface StudentCourseProgressApiResponse {
  progressPercentage: number;
  totalLessons: number;
  completedLessons: number;
}

export type FetchStudentCourseProgressFn = (
  courseId: string
) => Promise<StudentCourseProgressApiResponse>;

export interface UpdateLessonVideoProgressRequest {
  lastPosition: number;
  progressPercentage: number;
}

export type UpdateLessonVideoProgressFn = (
  lessonId: string,
  payload: UpdateLessonVideoProgressRequest
) => Promise<void>;

export type CompleteStudentLessonFn = (lessonId: string) => Promise<void>;

export interface StudentLessonTranslation {
  languageCode: string;
  title: string;
}

export interface StudentLessonFile {
  id: string;
  courseModuleLessonId: string;
  fileUrl: string;
  blobName: string;
  originalFileName: string;
  containerName: string;
  sizeInBytes: number;
  contentType: string;
  createdAt: string;
  updatedAt: string;
}

export interface StudentLessonDetailApiResponse {
  id: string;
  courseModuleId: string;
  type: number;
  typeName: string;
  videoUrl: string | null;
  points: number;
  displayOrder: number;
  translations: StudentLessonTranslation[];
  courseModuleLessonFiles: StudentLessonFile[];
  courseModuleLessonQuizConfig: unknown | null;
  createdAt: string;
  updatedAt: string;
}

export type FetchStudentLessonDetailFn = (
  lessonId: string
) => Promise<StudentLessonDetailApiResponse>;

/** Opção em POST v1/student/lessons/:lessonId/quiz-attempts. */
export interface StudentQuizAttemptOption {
  id: string;
  description: string;
}

/** Questão em POST v1/student/lessons/:lessonId/quiz-attempts. */
export interface StudentQuizAttemptQuestion {
  questionId: string;
  title: string;
  order: number;
  options: StudentQuizAttemptOption[];
}

/** Resposta de POST v1/student/lessons/:lessonId/quiz-attempts. */
export interface StudentQuizAttemptApiResponse {
  attemptId: string;
  questions: StudentQuizAttemptQuestion[];
}

export type StartStudentQuizAttemptFn = (
  lessonId: string
) => Promise<StudentQuizAttemptApiResponse>;

/** Resposta de GET v1/student/lessons/:lessonId/quiz-pre-attempt. */
export interface StudentQuizPreAttemptApiResponse {
  totalQuestions: number;
  minScore: number;
  maxAttempts: number;
  remainingAttempts: number;
}

export type FetchStudentQuizPreAttemptFn = (
  lessonId: string
) => Promise<StudentQuizPreAttemptApiResponse>;

/** Item do body em POST …/quiz-attempts/:id/submit-quiz. */
export interface StudentSubmitQuizAnswer {
  questionId: string;
  selectedOptionId: string;
}

/** Body de POST v1/student/quiz-attempts/:userQuizAttemptId/submit-quiz. */
export interface StudentSubmitQuizRequest {
  answers: StudentSubmitQuizAnswer[];
}

/** Resposta de POST v1/student/quiz-attempts/:userQuizAttemptId/submit-quiz. */
export interface StudentSubmitQuizApiResponse {
  approved: boolean;
  yourScore: number;
  minimumRequiredScore: number;
  xpEarned: number;
  remainingAttempts: number;
  totalAttempts: number;
}

/**
 * Finaliza a tentativa de quiz (POST v1/student/quiz-attempts/:userQuizAttemptId/submit-quiz).
 * `userQuizAttemptId` é o `attemptId` retornado por POST …/lessons/:lessonId/quiz-attempts.
 */
export type SubmitStudentQuizFn = (
  userQuizAttemptId: string,
  body: StudentSubmitQuizRequest
) => Promise<StudentSubmitQuizApiResponse>;

/** Cliente mínimo (get). Compatível com ApiClient do core. */
export interface StudentApiClient {
  get<T>(url: string): Promise<T>;
  post<T>(url: string, data?: unknown): Promise<T>;
}

const STUDENT_FEED_PATH = '/v1/student/feed';
const STUDENT_RANKING_PATH = '/v1/student/ranking';
const STUDENT_COURSE_PLAYER_PATH = '/v1/student/courses';
const STUDENT_LESSONS_PATH = '/v1/student/lessons';
const STUDENT_QUIZ_ATTEMPTS_PATH = '/v1/student/quiz-attempts';
const COURSE_MODULE_LESSON_PATH = '/v1/course-module-lesson';

/** Aceita camelCase ou PascalCase (API .NET). */
/** Aceita camelCase ou PascalCase (API .NET). */
function normalizeQuizPreAttemptResponse(
  raw: StudentQuizPreAttemptApiResponse | Record<string, unknown>
): StudentQuizPreAttemptApiResponse {
  const r = raw as Record<string, unknown>;
  const num = (camel: string, pascal: string) => {
    const v = r[camel] ?? r[pascal];
    return typeof v === 'number' ? v : Number(v ?? 0);
  };
  return {
    totalQuestions: num('totalQuestions', 'TotalQuestions'),
    minScore: num('minScore', 'MinScore'),
    maxAttempts: num('maxAttempts', 'MaxAttempts'),
    remainingAttempts: num('remainingAttempts', 'RemainingAttempts'),
  };
}

function normalizeSubmitQuizResponse(
  raw: StudentSubmitQuizApiResponse | Record<string, unknown>
): StudentSubmitQuizApiResponse {
  const r = raw as Record<string, unknown>;
  const num = (camel: string, pascal: string) => {
    const v = r[camel] ?? r[pascal];
    return typeof v === 'number' ? v : Number(v ?? 0);
  };
  const approved = r.approved ?? r.Approved;
  return {
    approved: approved === true || approved === 'true',
    yourScore: num('yourScore', 'YourScore'),
    minimumRequiredScore: num('minimumRequiredScore', 'MinimumRequiredScore'),
    xpEarned: num('xpEarned', 'XpEarned'),
    remainingAttempts: num('remainingAttempts', 'RemainingAttempts'),
    totalAttempts: num('totalAttempts', 'TotalAttempts'),
  };
}

function nameToInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    const w = parts[0];
    return w.slice(0, Math.min(2, w.length)).toUpperCase();
  }
  const a = parts[0][0];
  const b = parts[parts.length - 1][0];
  return `${a}${b}`.toUpperCase();
}

function mapRowToEntry(row: StudentRankingApiRow): StudentRankingEntry {
  return {
    id: row.userId,
    position: row.rank,
    initials: nameToInitials(row.name),
    name: row.name.trim(),
    xp: row.activePoints,
  };
}

function mapRowToCurrentUser(
  row: StudentRankingApiRow
): StudentRankingCurrentUser {
  return {
    position: row.rank,
    label: row.name.trim(),
    xp: row.activePoints,
  };
}

/**
 * Normaliza a resposta da API para as props de {@link StudentRankingPanel}.
 */
export function mapStudentRankingToPanelData(
  data: StudentRankingApiResponse | null | undefined
): {
  entries: StudentRankingEntry[];
  currentUser: StudentRankingCurrentUser | undefined;
} {
  if (!data) {
    return { entries: [], currentUser: undefined };
  }
  const entries = Array.isArray(data.top)
    ? data.top
        .map(mapRowToEntry)
        .sort((a, b) => a.position - b.position)
    : [];
  const currentUserRow = data.currentUser;
  const currentUser =
    currentUserRow != null ? mapRowToCurrentUser(currentUserRow) : undefined;
  return { entries, currentUser };
}

export function createStudentApi(api: StudentApiClient) {
  return {
    /**
     * Feed de cursos do aluno (GET v1/student/feed).
     * Accept / Accept-Language vêm do cliente HTTP do app.
     */
    async fetchFeed(): Promise<StudentFeedApiResponse> {
      const currentSearchParam =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('search') ?? ''
          : '';
      const normalizedSearch = currentSearchParam.trim();
      const path =
        normalizedSearch.length > 0
          ? `${STUDENT_FEED_PATH}?search=${encodeURIComponent(normalizedSearch)}`
          : STUDENT_FEED_PATH;
      return api.get<StudentFeedApiResponse>(path);
    },
    /**
     * Ranking (GET v1/student/ranking).
     * Accept / Accept-Language vêm do cliente HTTP do app.
     */
    async fetchRanking(): Promise<StudentRankingApiResponse> {
      return api.get<StudentRankingApiResponse>(STUDENT_RANKING_PATH);
    },
    /**
     * Player do curso do aluno (GET v1/student/courses/:courseId/player).
     */
    async fetchCoursePlayer(
      courseId: string
    ): Promise<StudentCoursePlayerApiResponse> {
      const encodedCourseId = encodeURIComponent(courseId);
      return api.get<StudentCoursePlayerApiResponse>(
        `${STUDENT_COURSE_PLAYER_PATH}/${encodedCourseId}/player`
      );
    },
    /**
     * Progresso do curso do aluno (GET v1/student/courses/:courseId/progress).
     */
    async fetchCourseProgress(
      courseId: string
    ): Promise<StudentCourseProgressApiResponse> {
      const encodedCourseId = encodeURIComponent(courseId);
      return api.get<StudentCourseProgressApiResponse>(
        `${STUDENT_COURSE_PLAYER_PATH}/${encodedCourseId}/progress`
      );
    },
    /**
     * Atualiza progresso de vídeo da lição (POST v1/student/lessons/:lessonId/video-progress).
     */
    async updateLessonVideoProgress(
      lessonId: string,
      payload: UpdateLessonVideoProgressRequest
    ): Promise<void> {
      const encodedLessonId = encodeURIComponent(lessonId);
      await api.post<void>(
        `${STUDENT_LESSONS_PATH}/${encodedLessonId}/video-progress`,
        payload
      );
    },
    /**
     * Marca lição como concluída (POST v1/student/lessons/:lessonId/complete).
     */
    async completeLesson(lessonId: string): Promise<void> {
      const encodedLessonId = encodeURIComponent(lessonId);
      await api.post<void>(
        `${STUDENT_LESSONS_PATH}/${encodedLessonId}/complete`
      );
    },
    /**
     * Detalhe da lição (GET v1/course-module-lesson/:lessonId).
     */
    async fetchLessonDetail(
      lessonId: string
    ): Promise<StudentLessonDetailApiResponse> {
      const encodedLessonId = encodeURIComponent(lessonId);
      return api.get<StudentLessonDetailApiResponse>(
        `${COURSE_MODULE_LESSON_PATH}/${encodedLessonId}`
      );
    },
    /**
     * Dados para o card pré-quiz (GET v1/student/lessons/:lessonId/quiz-pre-attempt).
     */
    async fetchQuizPreAttempt(
      lessonId: string
    ): Promise<StudentQuizPreAttemptApiResponse> {
      const encodedLessonId = encodeURIComponent(lessonId);
      const raw = await api.get<StudentQuizPreAttemptApiResponse>(
        `${STUDENT_LESSONS_PATH}/${encodedLessonId}/quiz-pre-attempt`
      );
      return normalizeQuizPreAttemptResponse(
        raw as StudentQuizPreAttemptApiResponse | Record<string, unknown>
      );
    },
    /**
     * Inicia tentativa de quiz (POST v1/student/lessons/:lessonId/quiz-attempts).
     */
    async startQuizAttempt(
      lessonId: string
    ): Promise<StudentQuizAttemptApiResponse> {
      const encodedLessonId = encodeURIComponent(lessonId);
      return api.post<StudentQuizAttemptApiResponse>(
        `${STUDENT_LESSONS_PATH}/${encodedLessonId}/quiz-attempts`
      );
    },
    /**
     * Envia o quiz ao concluir (POST v1/student/quiz-attempts/:userQuizAttemptId/submit-quiz).
     */
    async submitQuiz(
      userQuizAttemptId: string,
      body: StudentSubmitQuizRequest
    ): Promise<StudentSubmitQuizApiResponse> {
      const encodedId = encodeURIComponent(userQuizAttemptId);
      const raw = await api.post<StudentSubmitQuizApiResponse>(
        `${STUDENT_QUIZ_ATTEMPTS_PATH}/${encodedId}/submit-quiz`,
        body
      );
      return normalizeSubmitQuizResponse(
        raw as StudentSubmitQuizApiResponse | Record<string, unknown>
      );
    },
  };
}

export type StudentApi = ReturnType<typeof createStudentApi>;
